import { useLocale } from '@/components/Link'
import { getTruncatedLabel } from '@/components/page-specific/catering/supplier-menu/components/sidebar-filter/CatergoryFilter'
import { SidebarAccordion } from '@/components/ui/Accordion'
import { SidebarFilterChip } from '@/components/ui/Chip'
import { useTagsQuery } from '@/generated/graphql'
import useTenant from '@/hooks/useTenant'
import { getCountryFromLocale } from '@fusion/locale/parsing'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, Stack, Typography } from '@mui/material'

import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TagGroupNames, VendorSearchState } from '../../../states/useVendorSearchState'
import { getTranslatedTagLabel } from '../../../utils/tagTranslation'

const DISPLAY_LIMIT = 12

type Props = {
  tagType: TagGroupNames
  defaultOpen?: boolean
  useName?: boolean
  orderBy?: 'position' | 'label'
  specificToCountry?: boolean
  stats: VendorSearchState['stats']
  search: VendorSearchState['search']
  filters: VendorSearchState['filters']
  searching: boolean
}

const SupplierTagsFilter: React.FC<Props> = ({
  tagType,
  defaultOpen = false,
  useName = true,
  orderBy,
  specificToCountry,
  stats,
  search,
  filters,
  searching,
}) => {
  const tenant = useTenant()
  const locale = useLocale()
  const { t } = useTranslation()

  const getTranslatedChipLabel = (value: string) => {
    return getTranslatedTagLabel(
      {
        label: value,
        tagTypeLabel: tagType.label,
      },
      t,
    )
  }

  const countryCode = getCountryFromLocale(locale || '')

  const selectedValues = useMemo(() => {
    const raw = filters[tagType.key]
    return Array.isArray(raw) ? (raw as string[]) : []
  }, [filters, tagType.key])

  const tagFilter = useMemo(() => new Set(selectedValues), [selectedValues])

  const { data, fetchMore } = useTagsQuery({
    variables: {
      filters: {
        groupId: tagType.id,
        tenantId: tenant.id,
        ...(specificToCountry && {
          countryCode,
          isPublicFilterGm: true,
        }),
      },
      limit: 200,
      offset: 0,
      orderBy: orderBy || null,
    },
  })

  const tags = useMemo(() => data?.tags.rows || [], [data?.tags.rows])

  const uniqueTags = useMemo(() => {
    const seen = new Set<string>()
    return tags.filter((tag) => {
      const tagId = tag.id ?? ''
      if (!tagId || seen.has(tagId)) {
        return false
      }
      seen.add(tagId)
      return true
    })
  }, [tags])

  const filtersTotal = tagFilter.size
  const filterStats = stats ? stats[tagType.key] : null

  const [expanded, setExpanded] = useState(defaultOpen || filtersTotal > 0)
  const [displayCount, setDisplayCount] = useState(DISPLAY_LIMIT)

  useEffect(() => {
    if (filtersTotal > 0 && !expanded) {
      setExpanded(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visibleTags = useMemo(() => {
    return uniqueTags.filter((tag) => {
      const keyType = useName && tag.name ? 'name' : 'label'
      const title = tag[keyType]
      if (!title || !tag.groupId) return false
      return !!filterStats?.find((s) => s[keyType] === title)?.count
    })
  }, [uniqueTags, filterStats, useName])

  const tagsToShow = visibleTags.slice(0, displayCount)
  const hasMoreToShow = visibleTags.length > displayCount || (data?.tags.pageInfo.hasMore ?? false)

  const setNewTagFilter = (newTagFilter: Set<string>) => {
    void search({ [tagType.key]: Array.from(newTagFilter) })
  }

  const showMore = () => {
    setDisplayCount((c) => c + DISPLAY_LIMIT)
    // Only hits the backend if all 200 pre-fetched tags were exhausted (rare)
    if (visibleTags.length <= displayCount && data?.tags.pageInfo.hasMore) {
      void fetchMore({
        variables: {
          filters: {
            groupId: tagType.id,
            tenantId: tenant.id,
            ...(specificToCountry && {
              countryCode,
              isPublicFilterGm: true,
            }),
          },
          limit: 200,
          offset: uniqueTags.length,
          orderBy: orderBy || null,
        },
      })
    }
  }

  let filterLabel = tagType.label
  const translatedFilterLabel = t(`marketplace:${tagType.key}`)

  if (translatedFilterLabel !== tagType.label) {
    filterLabel = translatedFilterLabel
  }

  if (filtersTotal > 0) {
    filterLabel += ` (${filtersTotal})`
  }

  const renderChipLabel = (name: string, count: number) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
      <Typography variant="body3" fontWeight={500}>
        {getTruncatedLabel(getTranslatedChipLabel(name))} ({count})
      </Typography>
    </Box>
  )

  return (
    <SidebarAccordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      expandIcon={<ExpandMoreIcon />}
      label={filterLabel}
      summaryProps={{
        'aria-controls': `${tagType.key}-content`,
        id: `${tagType.key}-header`,
      }}>
      {tags.length ? (
        <Stack spacing={1.5}>
          <Stack
            sx={{
              gap: 1,
            }}>
            {tagsToShow.map((tag) => {
              const keyType = useName && tag.name ? 'name' : 'label'
              const title = tag[keyType]
              if (!title) return null
              const filterStatsCount = filterStats?.find(
                (tagStat) => tagStat[keyType] === title,
              )?.count
              const isSelected = tagFilter.has(title)

              return (
                <SidebarFilterChip
                  key={tag.id}
                  selected={isSelected}
                  onClick={() => {
                    const newSet = new Set(tagFilter)
                    if (isSelected) {
                      newSet.delete(title)
                    } else {
                      newSet.add(title)
                    }
                    setNewTagFilter(newSet)
                  }}
                  label={renderChipLabel(title, filterStatsCount ?? 0)}
                />
              )
            })}
          </Stack>
          {hasMoreToShow && !searching ? (
            <Box>
              <Button variant="text" size="small" onClick={showMore} disabled={searching}>
                {t('marketplace:showMore')}
              </Button>
            </Box>
          ) : null}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('marketplace:noFiltersAvailable', 'No options available')}
        </Typography>
      )}
    </SidebarAccordion>
  )
}

export default SupplierTagsFilter
export { SupplierTagsFilter }
